export const ITEM_DESCRIPTION_MAX_WORDS = 60;

export const ITEM_PRICE_TYPE_OPTIONS = [
  { value: "fixed", label: "Fixed price" },
  { value: "range", label: "Price range" },
  { value: "starting_from", label: "Starting from" },
  { value: "contact", label: "Contact for price" },
] as const;

export const ITEM_PRICE_UNIT_OPTIONS = [
  { value: "item", label: "Per item", suffix: "per item" },
  { value: "day", label: "Per day", suffix: "per day" },
  { value: "hour", label: "Per hour", suffix: "per hour" },
  { value: "session", label: "Per session", suffix: "per session" },
  { value: "person", label: "Per person", suffix: "per person" },
] as const;

export const CUSTOM_ITEM_PRICE_UNIT = "custom" as const;
export const CUSTOM_PRICE_UNIT_MAX_CHARS = 30;
export const CUSTOM_PRICE_UNIT_MAX_WORDS = 3;

export type ItemPriceType = (typeof ITEM_PRICE_TYPE_OPTIONS)[number]["value"];
export type PresetItemPriceUnit =
  (typeof ITEM_PRICE_UNIT_OPTIONS)[number]["value"];
export type ItemPriceUnit = PresetItemPriceUnit | typeof CUSTOM_ITEM_PRICE_UNIT;

export type ItemPricingFields = {
  description?: string | null;
  priceType?: ItemPriceType | null;
  priceAmount?: number | string | null;
  priceMin?: number | string | null;
  priceMax?: number | string | null;
  priceUnit?: ItemPriceUnit | null;
  customPriceUnit?: string | null;
};

export function countWords(value: string) {
  const words = value.trim().match(/\S+/g);
  return words ? words.length : 0;
}

export function normalizeItemDescription(value: string) {
  return value.trim().replace(/\s+/g, " ");
}

export function isItemPriceType(value: unknown): value is ItemPriceType {
  return ITEM_PRICE_TYPE_OPTIONS.some((option) => option.value === value);
}

export function isPresetItemPriceUnit(
  value: unknown
): value is PresetItemPriceUnit {
  return ITEM_PRICE_UNIT_OPTIONS.some((option) => option.value === value);
}

export function isItemPriceUnit(value: unknown): value is ItemPriceUnit {
  return isPresetItemPriceUnit(value) || value === CUSTOM_ITEM_PRICE_UNIT;
}

export function itemPriceTypeUsesAmount(type: ItemPriceType | null) {
  return type === "fixed" || type === "range" || type === "starting_from";
}

export function normalizeCustomPriceUnit(value: unknown) {
  if (typeof value !== "string") return "";

  const normalized = value.trim().replace(/\s+/g, " ");

  return normalized.replace(/^per(?:\s+|$)/i, "").trim();
}

export function validateCustomPriceUnit(value: unknown) {
  const normalized = normalizeCustomPriceUnit(value);

  if (!normalized) {
    return {
      value: "",
      error: "Enter a custom unit.",
    };
  }

  if (normalized.length > CUSTOM_PRICE_UNIT_MAX_CHARS) {
    return {
      value: normalized,
      error: `Keep the custom unit to ${CUSTOM_PRICE_UNIT_MAX_CHARS} characters or fewer.`,
    };
  }

  if (countWords(normalized) > CUSTOM_PRICE_UNIT_MAX_WORDS) {
    return {
      value: normalized,
      error: `Use ${CUSTOM_PRICE_UNIT_MAX_WORDS} words or fewer.`,
    };
  }

  return {
    value: normalized,
    error: null,
  };
}

export function readPositiveAmount(value: unknown) {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value !== "string") return null;

  const parsed = Number(value.replace(/,/g, "").trim());

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatAmount(value: number) {
  return value.toLocaleString("en-KE", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function getUnitSuffix(unit: unknown, customUnit: unknown) {
  if (unit === CUSTOM_ITEM_PRICE_UNIT) {
    const normalizedCustomUnit = normalizeCustomPriceUnit(customUnit);

    return normalizedCustomUnit ? `per ${normalizedCustomUnit}` : "";
  }

  const option = ITEM_PRICE_UNIT_OPTIONS.find((entry) => entry.value === unit);
  return option?.suffix ?? "";
}

function withUnit(label: string, unit: unknown, customUnit: unknown) {
  const suffix = getUnitSuffix(unit, customUnit);
  return suffix ? `${label} ${suffix}` : label;
}

export function formatItemPrice(item?: ItemPricingFields | null) {
  if (!item || !isItemPriceType(item.priceType)) return "";

  if (item.priceType === "contact") {
    return "Contact for price";
  }

  if (item.priceType === "range") {
    const min = readPositiveAmount(item.priceMin);
    const max = readPositiveAmount(item.priceMax);

    if (min === null || max === null || min > max) return "";

    return withUnit(
      `KSh ${formatAmount(min)} – ${formatAmount(max)}`,
      item.priceUnit,
      item.customPriceUnit
    );
  }

  const amount = readPositiveAmount(item.priceAmount);

  if (amount === null) return "";

  if (item.priceType === "starting_from") {
    return withUnit(
      `From KSh ${formatAmount(amount)}`,
      item.priceUnit,
      item.customPriceUnit
    );
  }

  return withUnit(
    `KSh ${formatAmount(amount)}`,
    item.priceUnit,
    item.customPriceUnit
  );
}
